import { CircularProgress } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import { Autocomplete } from '@material-ui/lab';
import { RenderInputParams } from '@material-ui/lab/Autocomplete';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import * as React from 'react';
import { IAutoCompleteItem } from '../../typings/autoComplete';
import { DropDownData } from '../../featureConfigurationLayout/FeatureLayoutConfigurationRepository';
import { UserFeature, UserFeatureEnum } from '../../types/UserFeature';

export interface ScopeSelectorProps {
    onSelection?: (selection: IAutoCompleteItem, name: string) => void;
    // tslint:disable-next-line: no-any
    items?: any;
    required?: boolean;
    label?: string;
    name: string;
    value: string;
    disabled?: boolean;
    className?: string;
    // tslint:disable-next-line: no-any
    hideScope?: any;
    defaultValue?: IAutoCompleteItem;
    variant?: 'standard' | 'outlined' | 'filled';
    size?: 'small' | 'medium';
    excludeScope?: Array<'DashboardSecurityScope' | 'DashboardSecurityRoleCategory' | 'DashboardSecurityRole' | 'DashboardSecurityUser'>;
}

interface SelectorState {
    selectedCode: string;
    selectedLabel: string;
}

export type ScopeValue = 'DashboardSecurityScope' | 'DashboardSecurityRoleCategory' | 'DashboardSecurityRole' | 'DashboardSecurityUser';

function fetchScopeFeatureData(): DropDownData[] {
    var scope = [];

    // This is selected through features. Which are passed on through the accessToken while logging in.
    if (UserFeature.HasAccess(UserFeatureEnum.hasEmailTemplateSecurityScope) ) {
        scope.push({value: 'DashboardSecurityScope', text: 'Firm'});
    }
    if (UserFeature.HasAccess(UserFeatureEnum.hasEmailTemplateSecurityRoleCategory) ) {
        scope.push({value: 'DashboardSecurityRoleCategory', text: 'Role Category'});
    }
    if (UserFeature.HasAccess(UserFeatureEnum.hasEmailTemplateSecurityRole) ) {
        scope.push({value: 'DashboardSecurityRole', text: 'Role'});
    }
    if (UserFeature.HasAccess(UserFeatureEnum.hasEmailTemplateSecurityUser) ) {
        scope.push({value: 'DashboardSecurityUser', text: 'User'});
    }

    return scope;
}

export class ScopeSelector extends React.Component<ScopeSelectorProps, SelectorState> {
    // tslint:disable-next-line: no-any
    public scopeData: any;

    constructor(props: ScopeSelectorProps, state?: SelectorState) {
        super(props, state);
        
        this.handleChange = this.handleChange.bind(this);

        const initialSelectedCode = props.value || (props.defaultValue ? props.defaultValue.value : '');
        const initialOptions = this.getScopeOptions(props);
        const initialSelectedItem = initialOptions.find((item: IAutoCompleteItem) => item.value === initialSelectedCode);
        const initialSelectedLabel = initialSelectedItem
            ? initialSelectedItem.label
            : (props.defaultValue && props.defaultValue.value === initialSelectedCode ? props.defaultValue.label : '');
        
        this.state = {
            selectedCode: initialSelectedCode,
            selectedLabel: initialSelectedLabel
        };
    }

    public shouldComponentUpdate(nextProps: ScopeSelectorProps) {
        if (this.props.value !== nextProps.value) {
            return true;
        }

        const currentHidden = this.props.hideScope ? this.props.hideScope.join('|') : '';
        const nextHidden = nextProps.hideScope ? nextProps.hideScope.join('|') : '';

        if (currentHidden !== nextHidden) {
            return true;
        }

        const currentExcluded = this.props.excludeScope ? this.props.excludeScope.join('|') : '';
        const nextExcluded = nextProps.excludeScope ? nextProps.excludeScope.join('|') : '';

        if (currentExcluded !== nextExcluded) {
            return true;
        }

        return false;
    }

    public UNSAFE_componentWillReceiveProps(nextProps: ScopeSelectorProps) {
        if (this.props.value !== nextProps.value) {
            const scopeOptions = this.getScopeOptions(nextProps);
            const filteredItem = scopeOptions.find((item: IAutoCompleteItem) => item.value.toLowerCase() === (nextProps.value.toLowerCase()));

            if (filteredItem && nextProps.value.toString() !== '') {
                this.setState({
                    selectedCode: filteredItem.value,
                    selectedLabel: filteredItem.label
                });
            } else {
                this.setState({
                    selectedCode: '',
                    selectedLabel: ''
                });
            }           
        }
    }

    public render() {

        this.scopeData = this.getScopeOptions(this.props);
        
        return (
            <div className={'downshift-wrapper'}>
                <Autocomplete
                    loading={false}
                    disabled={this.props.disabled}
                    autoSelect={true}
                    value={{value: this.state.selectedCode, label: this.state.selectedLabel}}
                    onChange={this.handleChange}
                    options={this.scopeData}
                    className={this.props.className}
                    // tslint:disable-next-line: jsx-no-lambda
                    getOptionLabel={option => option.label}         
                    size={this.props.size || 'medium'}
                    // tslint:disable-next-line: jsx-no-lambda
                    renderInput={params => (
                        <TextField 
                            {...params} 
                            label={this.props.label} 
                            variant={this.props.variant || 'standard'} 
                            fullWidth={true} 
                            margin="none" 
                            required={this.props.required}
                            InputProps={this.getInputProps(params, false)}  
                            size={this.props.size || 'medium'}
                        />
                    )}
                    // tslint:disable-next-line: jsx-no-lambda
                    renderOption={(option, { inputValue }) => {
                        const matches = match(option.label, inputValue);
                        const parts = parse(option.label, matches);
                
                        return (
                          <div>
                            {parts.map((part, index) => (
                              <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                                {part.text}
                              </span>
                            ))}
                          </div>
                        );
                      }}
                />
            </div>
        );
    }

    private getScopeOptions(props: ScopeSelectorProps): IAutoCompleteItem[] {
        let scopesList: DropDownData[] = fetchScopeFeatureData();

        if (props.hideScope) {
            scopesList = scopesList.filter((x: DropDownData) => !props.hideScope.includes(x.value));
        }

        if (props.excludeScope && props.excludeScope.length > 0) {
            scopesList = scopesList.filter((x: DropDownData) => !props.excludeScope!.includes(x.value as ScopeValue));
        }

        return scopesList.map((scope: DropDownData) => ({
            value: scope.value,
            label: scope.text,
        }));
    }

    private getInputProps(params: RenderInputParams, isLoading?: boolean) {
        return  ({   
            ...params.InputProps,
            endAdornment: (
                <React.Fragment>
                  {isLoading ? <CircularProgress color="primary" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),     
         });
    }

    // tslint:disable-next-line: no-any
    private handleChange(event: object, item: any) {        
        this.setState( {
            selectedCode: item ? item.value : '',
            selectedLabel: item ? item.label : ''
        });

        if (this.props.onSelection) {
            this.props.onSelection({label: item ? item.label : '', value: item ? item.value : ''},
                                   this.props.name);
        }     
    }
}